import React, { useState } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useAccount } from 'wagmi';
import { ReactComponent as VeloraLogoDark } from '../assets/svg/velora_logo-dark.svg';
import { ReactComponent as VeloraLogoLight } from '../assets/svg/velora_logo-light.svg';
import { ReactComponent as VeloraIconDark } from '../assets/svg/velora_icon-dark.svg';
import { ReactComponent as VeloraIconLight } from '../assets/svg/velora_icon-light.svg';
import { ReactComponent as WalletIcon } from '../assets/svg/wallet.svg';
import ConnectedWalletModal from './ConnectedWalletModal';

const Header: React.FC = () => {
  const { theme, toggleTheme } = useTheme();
  const { isConnected, address } = useAccount();
  const [isWalletModalOpen, setIsWalletModalOpen] = useState<boolean>(false);

  const formatAddress = (address: string | undefined, isMobile: boolean = false): string => {
    if (!address) return '';
    if (isMobile) {
      return `${address.slice(0, 6)}...`;
    }
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };
  
  return (
    <div className="absolute left-0 top-0 right-0 z-20">
      <div className="absolute h-[88px] left-0 right-0 top-0 bg-gradient-to-b from-[rgba(74,93,249,0.05)] to-transparent" />
      
      {/* Logo with Distribution Tag */}
      <div className="absolute left-4 md:left-8 top-6 flex items-center gap-3">
        {/* Mobile Icon */}
        <div className="md:hidden h-10 w-10">
          {theme === 'dark' ? (
            <VeloraIconDark className="h-10 w-10" />
          ) : (
            <VeloraIconLight className="h-10 w-10" />
          )}
        </div>
        {/* Desktop Logo */}
        <div className="hidden md:block h-10 w-[155px]">
          {theme === 'dark' ? (
            <VeloraLogoDark className="h-full w-auto" />
          ) : (
            <VeloraLogoLight className="h-full w-auto" />
          )}
        </div>
        
        {/* Distribution Tag */}
        <button
          onClick={() => window.location.reload()}
          className="relative rounded-[17px] p-[1px] shadow-[0px_2px_12px_0px_rgba(15,17,26,0.05)] cursor-pointer"
          style={{
            background: 'linear-gradient(45deg, rgba(247, 92, 42, 0.8) 0%, rgba(74, 93, 249, 0.8) 100%)'
          }}
        >
          <div className="relative px-2.5 py-1.5 rounded-[15px] bg-contrast-8 dark:bg-contrast-2">
            <p className="text-contrast-4 dark:text-white font-inter font-medium text-[11px] leading-none whitespace-nowrap">
              DISTRIBUTION
            </p>
          </div>
        </button>
      </div>
      
      {/* User Menu */}
      <div className="absolute flex gap-2 md:gap-4 items-center right-4 md:right-8 top-6">
        
        {/* Light/Dark Mode Switch */}
        <button 
          onClick={toggleTheme}
          className="flex gap-2 h-10 items-center justify-center px-3 py-2.5 rounded-lg border border-contrast-6 dark:border-contrast-5 hover:bg-contrast-7 dark:hover:bg-contrast-2 transition-colors"
          aria-label="Toggle theme"
        >
          {theme === 'dark' ? (
            /* Sun icon for light mode */
            <svg className="w-5 h-5 text-contrast-3 dark:text-contrast-7" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="10.5" cy="10.5" r="4" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M10.5 1.5V3.5M10.5 17.5V19.5M3.5 10.5H1.5M19.5 10.5H17.5M5.11 5.11L3.69 3.69M17.31 17.31L15.89 15.89M5.11 15.89L3.69 17.31M17.31 3.69L15.89 5.11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          ) : (
            /* Moon icon for dark mode - from moon_icon.svg */
            <svg className="w-5 h-5" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M5.70801 5.38482C5.70801 4.67519 5.77004 3.94894 5.91406 3.24712C3.89707 4.64562 2.59961 7.02973 2.59961 9.69243C2.59965 13.9491 6.05097 17.4004 10.3076 17.4004C12.9704 17.4004 15.3535 16.1021 16.752 14.085C16.0504 14.2289 15.3246 14.292 14.6152 14.292C9.69591 14.292 5.70809 10.3041 5.70801 5.38482ZM6.90723 5.38482C6.90731 9.6414 10.3586 13.0927 14.6152 13.0928C15.7412 13.0928 16.8451 12.9251 17.7607 12.5264C17.9866 12.4283 18.2495 12.4784 18.4238 12.6524C18.5982 12.8268 18.6482 13.0904 18.5498 13.3165C17.1723 16.4808 13.9507 18.5997 10.3076 18.5997C5.38823 18.5996 1.40043 14.6118 1.40039 9.69243C1.40039 6.04936 3.51928 2.82774 6.68359 1.45025C6.90969 1.35183 7.1733 1.40185 7.34766 1.57622C7.52163 1.7505 7.57178 2.01346 7.47363 2.23931C7.07497 3.15496 6.90723 4.2589 6.90723 5.38482Z" fill="currentColor" className="fill-contrast-3 dark:fill-contrast-7"/>
            </svg>
          )}
        </button>

        {/* Wallet Address Button - Only show when connected */}
        {isConnected && (
          <button 
            onClick={() => setIsWalletModalOpen(true)}
            className="flex gap-2 h-10 items-center px-3 md:px-4 py-2.5 rounded-lg border border-contrast-6 dark:border-contrast-5 hover:bg-contrast-7 dark:hover:bg-contrast-2 transition-colors"
            aria-label="Open wallet modal"
          >
            <WalletIcon className="w-5 h-5 hidden md:block" />
            <span className="text-contrast-4 dark:text-white text-[14px] font-medium font-geist-mono">
              <span className="md:hidden">{formatAddress(address, true)}</span>
              <span className="hidden md:inline">{formatAddress(address)}</span>
            </span>
          </button>
        )}
      </div>

      {/* Connected Wallet Modal */}
      <ConnectedWalletModal 
        isOpen={isWalletModalOpen} 
        onClose={() => setIsWalletModalOpen(false)} 
      />
    </div>
  );
};

export default Header;